import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TaxReturnTypeService, TaxReturnType } from "../../../../../swaggerServices";
import { takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import { UntypedFormBuilder, Validators, UntypedFormGroup, AbstractControl } from "@angular/forms";
import { ConfigStateService } from "../../../config/config-state.service";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";
import { environment } from '../../../../environments/environment';
import { TranslocoService} from "@jsverse/transloco";

@Component({
    selector: 'app-add-update-tax-return-type',
    templateUrl: './add-update-tax-return-type.component.html',
    styleUrls: ['./add-update-tax-return-type.component.scss'],
    standalone: false
})
export class AddUpdateTaxReturnTypeComponent implements OnInit, OnDestroy {

  taxReturnType: TaxReturnType;

  form: UntypedFormGroup;
  isCreate: boolean = false;
  isProductionMode: boolean;
  syncToOneTeamEnabled: boolean = false;
  syncToOneTeamFlag: boolean = false;

  taxReturnTypeStatusList = [
    { id: 1, value: 0, name: 'Active' },
    { id: 2, value: 99, name: 'Inactive' },
  ];
  subscriptions: Subscription[] = [];
  uiConfig: TenantConfigService;
  taxReturnAlias: string;
  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private config: ConfigStateService,
    private taxReturnTypeService: TaxReturnTypeService,
    private translocoService: TranslocoService,
    private dialogRef: MatDialogRef<AddUpdateTaxReturnTypeComponent>,
    @Inject(MAT_DIALOG_DATA) taxReturnType: TaxReturnType) {
    this.taxReturnType = taxReturnType;
    this.isProductionMode = environment.production;
  }

  get taxReturnTypeName(): AbstractControl {
    return this.form.get('taxReturnTypeName');
  }

  get taxReturnTypeKey(): AbstractControl {
    return this.form.get('taxReturnTypeKey');
  }

  get taxReturnTypeStatus(): AbstractControl {
    return this.form.get('taxReturnTypeStatus');
  }

  get syncToOneTeam(): AbstractControl {
    return this.form.get('syncToOneTeam');
  }

  get oneTeamEngagementType(): AbstractControl {
    return this.form.get('oneTeamEngagementType');
  }

  get oneTeamEngagementTypeDisplayName(): AbstractControl {
    return this.form.get('oneTeamEngagementTypeDisplayName');
  }

  ngOnInit() {
    this.isCreate = !this.taxReturnType;
    this.syncToOneTeamEnabled = this.config.featureFlags.enableOneteamIntegration;
    this.initForm();
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias
        this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
      })
    );
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.subscriptions.forEach(s => s.unsubscribe());

  }

  saveTaxReturnType() {
    if (this.isCreate) {
      const postData = {
        type: this.taxReturnTypeName.value,
        alias: this.taxReturnTypeKey.value,
        status: this.taxReturnTypeStatus.value ? this.taxReturnTypeStatus.value.value : this.taxReturnTypeStatus.value,
        syncToOneTeam: this.syncToOneTeam.value,
        oneTeamEngagementType: this.oneTeamEngagementType.value,
        oneTeamEngagementTypeDisplayName: this.oneTeamEngagementTypeDisplayName.value,
      };
      this.taxReturnTypeService.addTaxReturnType(postData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(result => {
          this.dialogRef.close(true);
        }, error => {
          this.dialogRef.close(false);
        });
    } else {
      const postData = {
        id: this.taxReturnType.id,
        type: this.taxReturnTypeName.value,
        alias: this.taxReturnTypeKey.value,
        status: this.taxReturnTypeStatus.value ? this.taxReturnTypeStatus.value.value : this.taxReturnTypeStatus.value,
        syncToOneTeam: this.syncToOneTeam.value,
        oneTeamEngagementType: this.oneTeamEngagementType.value,
        oneTeamEngagementTypeDisplayName: this.oneTeamEngagementTypeDisplayName.value,
      };
      this.taxReturnTypeService.updateTaxReturnType(postData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(result => {
          this.dialogRef.close(true);
        }, error => {
          this.dialogRef.close(false);
        });
    }
  }

  onClose() {
    this.dialogRef.close(false);
  }

  formatter(option) {
    return option.label;
  }

  toggleSyncToOneTeam() {
    this.syncToOneTeamFlag = !this.syncToOneTeamFlag;
    this.syncToOneTeam.setValue(this.syncToOneTeamFlag);
  }

  private initForm() {
    this.form = this.formBuilder.group({
      taxReturnTypeKey: ['', Validators.required],
      taxReturnTypeName: ['', Validators.required],
      taxReturnTypeStatus: ['', Validators.required],
      syncToOneTeam: [false],
      oneTeamEngagementType: [''],
      oneTeamEngagementTypeDisplayName: [''],
    });

    if (!this.isCreate) {
      this.taxReturnTypeKey.setValue(this.taxReturnType.alias);
      this.taxReturnTypeName.setValue(this.taxReturnType.type);
      this.taxReturnTypeStatus.setValue(this.taxReturnTypeStatusList.find(tst => tst.value === this.taxReturnType.status).value);
      this.syncToOneTeamFlag = !!this.taxReturnType.syncToOneTeam;
      this.syncToOneTeam.setValue(this.syncToOneTeamFlag);
      this.oneTeamEngagementType.setValue(this.taxReturnType.oneTeamEngagementType);
      this.oneTeamEngagementTypeDisplayName.setValue(this.taxReturnType.oneTeamEngagementTypeDisplayName);
    }
  }

}
